/* eslint-disable react-hooks/exhaustive-deps */
import { Select, Text } from "@mantine/core";
import { useStyles } from "./IndividualDetails.styles";
import { FC, useState, useEffect } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { Button, color } from "@funded-here-interface/common";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Data, UserData } from "../../types/org.type";
import { kycStatuses } from "@funded-here-interface/common/src/constant/constant";
import BankDetails from "../KycTabs/BankDetails/BankDetails";
import Documents from "../KycTabs/Documents/Documents";
import { KYCStatus } from "@funded-here-interface/common/src/constant/enum";
import Individual from "../KycTabs/Individual/Individual";
import useGetOrgMutation from "../../hooks/useGetOrg";
import useUpdateKyc from "../../hooks/useUpdateKyc";
import Agreements from "../KycTabs/Agreements/Agreements";
import useOrganizationRisk from "../../hooks/useOrganizationRisk";
import { Risk } from "../../constant/enum";
import { riskValues } from "../../constant/risk";
import InfoDocWallex from "../KycTabs/InfoDocWallex/InfoDocWallex";
import { RegtankOnboardingStatusEnum } from "@funded-here-interface/common/src/constant/enum";
import Note from "../KycTabs/Note/Note";
import RemarkHistory from "../KycTabs/RemarksHistory/RemarkHistory";
import EmailContentHistory from "../KycTabs/EmailContent/EmailContentHistory";
import EmailContent from "../KycTabs/EmailContent/EmailContent";
import Regtank from "../KycTabs/Regtank/Regtank";

interface IProps {
  close: () => void;
  id: number;
  userId: number | null;
  refetchList: () => void;
}

const IndividualDetails: FC<IProps> = ({ close, id, refetchList, userId }) => {
  const [tab, setTab] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setdata] = useState<Data>();
  const [userData, setUserData] = useState<UserData>();
  const [allUsersData, setAllUsersData] = useState<UserData[]>();
  const { token, regtankOnboardingFeatureFlag } = useSelector(
    (state: RootState) => state.auth
  );
  const getOrgMutation = useGetOrgMutation();
  const udpdateKycMutation = useUpdateKyc();
  const useOrganizationRiskMutation = useOrganizationRisk();
  const [selectedRisk, setSelectedRisk] = useState<Risk | undefined>();

  const [orgNote, setOrgNote] = useState<string>("");
  const { classes } = useStyles();
  const [disableSaveBtn, setDisableSaveBtn] = useState(false);

  const [selectedValue, setSelectedValue] = useState<string>(
    data?.kycStatus || KYCStatus.PENDING_APPROVAL
  );

  useEffect(() => {
    if (data?.kycStatus) {
      setSelectedValue(data.kycStatus);
    }
  }, [data?.kycStatus]);

  const handleSelectChange = (value: string) => {
    setSelectedValue(value);
  };

  useEffect(() => {
    getOrgMutation.mutate(
      {
        token,
        id: `${id}`,
      },
      {
        onSuccess: (data) => {
          setdata(data);
          setAllUsersData(data.user);
          setUserData(data.user[0]);
          setSelectedRisk(data?.risk);
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  }, [tab]);

  const handleRiskSelect = (risk: Risk) => {
    setSelectedRisk(risk);

    useOrganizationRiskMutation.mutate(
      { token, risk, orgId: data?.id! },
      {
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  const handleNext = async () => {
    if (selectedValue === KYCStatus.APPROVED) {
      const userRegtankOnboarding = userData?.regtankOnboarding;
      if (
        regtankOnboardingFeatureFlag === true &&
        userRegtankOnboarding === true
      ) {
        const userRegtankRequest = userData?.regtankRequest?.sort(
          (a, b) =>
            new Date(b.expiredDate).getTime() -
            new Date(a.expiredDate).getTime()
        );
        if (userRegtankRequest && userRegtankRequest.length <= 0) {
          toast.error("User has not setup Regtank");
          return;
        }
        if (
          userRegtankRequest?.[0].onboardingStatus !==
          RegtankOnboardingStatusEnum.APPROVED
        ) {
          toast.error(
            "The user has not completed Regtank onboarding identity verification"
          );
          return;
        }
      }

      if (!userData?.declarationAISubmitted) {
        toast.error("Investor Agreement has not been signed");

        return;
      }
    }

    if (
      selectedValue === KYCStatus.PENDING_APPROVAL ||
      selectedValue === data?.kycStatus
    ) {
      close();

      return;
    }

    const orgRoleId = userData?.orgRole?.[0]?.id;

    setIsLoading(true);
    udpdateKycMutation.mutate(
      {
        orgRoleId: orgRoleId as number,
        token,
        kycStatus: selectedValue as KYCStatus,
      },
      {
        onSuccess: () => {
          setIsLoading(false);
          refetchList();
          close();
        },
        onError: (e) => {
          setIsLoading(false);
          toast.error((e as Error).message);
        },
      }
    );
  };

  const signedDocuments = data?.documentsUpload?.signedDocuments
    ? data?.documentsUpload?.signedDocuments.filter(
        (doc) =>
          !(
            doc.docName === "membership_agreement" &&
            !userData?.agreedToMembershipAgreement
          )
      )
    : [];

  return (
    <div className={classes.OuterContainer}>
      <div className={classes.innerContainer}>
        <ToastContainer />

        <AiOutlineClose
          onClick={() => {
            close();
            setSelectedValue("");
          }}
          className={classes.closeIcon}
        />

        <Text className={classes.heading}>Individual KYC Details</Text>
        <div className={classes.SubHeadingOutterContainer}>
          <div className={classes.subHeadingContainer}>
            <Text className={classes.subHeadingText}>KYC Status</Text>
            <Select
              className={classes.KycSelect}
              data={kycStatuses.map((status) => ({
                value: status,
                label: status,
                disabled: status === KYCStatus.PENDING_RESUBMISSION,
              }))}
              value={selectedValue}
              onChange={handleSelectChange}
              size="md"
              variant="filled"
              radius="md"
              disabled={data?.kycStatus === KYCStatus.PENDING_RESUBMISSION}
              styles={{
                dropdown: {
                  minWidth: "200px", // Ensure dropdown is wide enough for longer text
                },
                item: {
                  whiteSpace: "nowrap", // Prevent text wrapping in dropdown items
                },
              }}
            />
            {data?.kycStatus === KYCStatus.PENDING_RESUBMISSION && (
              <Text size="sm" color="dimmed" mt={4}>
                Status locked. Awaiting user resubmission.
              </Text>
            )}
          </div>
          <div className={classes.subHeadingContainer}>
            <Text className={classes.subHeadingText}>Customer Risk</Text>
            <Select
              className={classes.KycSelect}
              data={riskValues}
              value={selectedRisk}
              placeholder="Select Risk"
              onChange={handleRiskSelect}
              size="md"
            />
          </div>
        </div>
        <div>
          <ul className={classes.listContainer}>
            <li
              onClick={() => setTab(1)}
              className={`${classes.listText} ${
                tab === 1 && classes.stepActive
              }`}
            >
              Individual
            </li>

            <li
              onClick={() => setTab(2)}
              className={`${classes.listText} ${
                tab === 2 && classes.stepActive
              }`}
            >
              Bank Details
            </li>
            <li
              onClick={() => setTab(3)}
              className={`${classes.listText} ${
                tab === 3 && classes.stepActive
              }`}
            >
              Documents
            </li>
            <li
              onClick={() => setTab(4)}
              className={`${classes.listText} ${
                tab === 4 && classes.stepActive
              }`}
            >
              Regtank Verification
            </li>
            {signedDocuments.length > 0 && (
              <li
                onClick={() => setTab(5)}
                className={`${classes.listText} ${
                  tab === 5 && classes.stepActive
                }`}
              >
                Agreements
              </li>
            )}
            {selectedValue === KYCStatus.APPROVED &&
              data?.kycStatus === "approved" && (
                <li
                  onClick={() => setTab(6)}
                  className={`${classes.listText} ${
                    tab === 6 && classes.stepActive
                  }`}
                >
                  Info & Doc to Wallex
                </li>
              )}
            <li
              onClick={() => setTab(9)}
              className={`${classes.listText} ${
                tab === 9 && classes.stepActive
              }`}
            >
              Remarks
            </li>
            <li
              onClick={() => setTab(10)}
              className={`${classes.listText} ${
                tab === 10 && classes.stepActive
              }`}
            >
              Remarks History
            </li>
          </ul>
          <div className={classes.contentContainer}>
            {tab === 1 && <Individual data={data} userData={userData} />}
            {tab === 2 && <BankDetails userData={userData} />}
            {tab === 3 && <Documents data={data} userData={userData} />}
            {tab === 4 && <Regtank orgData={data} userData={allUsersData} />}
            {tab === 6 && (
              <InfoDocWallex
                data={data}
                userData={userData}
                user={allUsersData}
                spv={data?.spv}
              />
            )}
            {signedDocuments.length > 0 && tab === 5 && (
              <Agreements signedDocuments={signedDocuments} />
            )}
            {tab === 9 && (
              <div>
                <Text className={classes.subHeadingText}>Internal Remarks</Text>
                <Note userId={userId} initialNote={orgNote} type="regular" />
                <div style={{ marginTop: "20px" }}>
                  <Text className={classes.subHeadingText}>
                    Remarks for Onboarding Details Resubmission
                  </Text>
                  {/* Email call here*/}
                  <EmailContent
                    data={data}
                    userData={userData}
                    companyName={""}
                  />
                </div>
              </div>
            )}
            {tab === 10 && (
              <div>
                <Text className={classes.subHeadingText}>Internal Remarks</Text>
                <RemarkHistory userId={userId} type="regular" />
                {data?.spv && (
                  <div style={{ marginTop: "20px" }}>
                    <Text className={classes.subHeadingText}>
                      Internal Remarks (SPV)
                    </Text>
                    <RemarkHistory userId={userId} type="spv" />
                  </div>
                )}
                <div style={{ marginTop: "20px" }}>
                  <Text className={classes.subHeadingText}>
                    Remarks for Onboarding Details Resubmission
                  </Text>
                  {/* Email call here*/}
                  <EmailContentHistory userId={userId} />
                </div>
              </div>
            )}
          </div>
          {tab === 1 && (
            <div className={classes.buttonWrapper}>
              <Button
                disabled={isLoading || disableSaveBtn}
                onClick={handleNext}
                backgroundColor={color.FHADMIN}
                textColor={color.WHITE}
                width="80%"
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IndividualDetails;
